import { Button } from '@HoudiniUI/Button';
import { Dialog, DialogBody, DialogFooter, DialogPopup, DialogTitle } from '@HoudiniUI/Dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@HoudiniUI/Select';
import {
	DynamicsUserMapImportOutcome,
	IDynamicsUserMapImportResult,
	useImportDynamicsUserMapsMutation,
} from '@Queries/index';
import { Badge } from '@WebComponents/dataBoards/Badge';
import { LoadingSpinner } from '@WebComponents/LoadingSpinner';
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { getDisplayName } from '../../../../../../../../models/NameUtils';
import { THEME } from '../../../../../../../../web/components-v2/Theme/theme';
import { styleSheet } from './styles';

interface IImportUserMapsModalProps {
	accountId: string;
	usersById: Map<string, Api.IUserWithStats>;
	/** Whether the grid behind this modal has unsaved edits. The import writes immediately, so the two cannot both be pending. */
	hasUnsavedMappingEdits: boolean;
	isOpen: boolean;
	onClose: () => void;
	onImported: () => void;
}

const ACCEPTED_FILE_TYPES = '.xlsx,.csv';
const ALL_RESULTS = 'all';
const UNRESOLVED_RESULTS = 'unresolved';

const outcomeLabels: Record<DynamicsUserMapImportOutcome, string> = {
	[DynamicsUserMapImportOutcome.Unknown]: 'Unresolved',
	[DynamicsUserMapImportOutcome.Mapped]: 'Will map',
	[DynamicsUserMapImportOutcome.OwnerNotFound]: 'Owner not found',
	[DynamicsUserMapImportOutcome.TargetUserNotFound]: 'No Levitate user',
	[DynamicsUserMapImportOutcome.TargetAmbiguous]: 'Ambiguous user',
	[DynamicsUserMapImportOutcome.OwnerAmbiguous]: 'Ambiguous owner',
	[DynamicsUserMapImportOutcome.Incomplete]: 'Missing value',
	[DynamicsUserMapImportOutcome.DuplicateOwner]: 'Owner listed twice',
	[DynamicsUserMapImportOutcome.MultipleTargets]: 'Multiple advisors listed',
	[DynamicsUserMapImportOutcome.Reassigned]: 'Reassigned',
};

// Three states, not two: a reassignment is applied like a mapping but needs a second look, so it must not wear the
// same colour as the rows that are being skipped.
const outcomeColor = (outcome: DynamicsUserMapImportOutcome) => {
	if (outcome === DynamicsUserMapImportOutcome.Mapped) {
		return THEME.colors.success.main;
	}
	return outcome === DynamicsUserMapImportOutcome.Reassigned ? THEME.colors.primary.main : THEME.colors.warning.main;
};

// Mirrors the server: a reassignment is applied like any other mapping, so it is not an unresolved row.
const willMap = (outcome: DynamicsUserMapImportOutcome) =>
	outcome === DynamicsUserMapImportOutcome.Mapped || outcome === DynamicsUserMapImportOutcome.Reassigned;

export const ImportUserMapsModal: React.FC<IImportUserMapsModalProps> = ({
	accountId,
	usersById,
	hasUnsavedMappingEdits,
	isOpen,
	onClose,
	onImported,
}) => {
	const [file, setFile] = React.useState<File | null>(null);
	const [preview, setPreview] = React.useState<IDynamicsUserMapImportResult | null>(null);
	const [rowFilter, setRowFilter] = React.useState<string>(ALL_RESULTS);
	const fileInputRef = React.useRef<HTMLInputElement>(null);
	const selectedFileRef = React.useRef<File | null>(null);

	const importMutation = useImportDynamicsUserMapsMutation({
		onError: error => toast.error(error.systemMessage ?? 'Failed to read the spreadsheet'),
		onSuccess: (result, params) => {
			if (params.commit) {
				toast.success(`Mapped ${result.mappedCount.toLocaleString()} rows`);
				onImported();
				reset();
				onClose();
				return;
			}

			if (params.file !== selectedFileRef.current) {
				return;
			}

			setPreview(result);
		},
	});

	const reset = () => {
		selectedFileRef.current = null;
		setFile(null);
		setPreview(null);
		setRowFilter(ALL_RESULTS);
	};

	const onFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
		const selected = event.target.files?.[0] ?? null;

		// Clearing the input lets the same file be chosen again: the browser only raises change when the value differs,
		// and re-picking a corrected spreadsheet under its original name is the normal way out of a rejected upload.
		event.target.value = '';

		setPreview(null);
		setRowFilter(ALL_RESULTS);
		selectedFileRef.current = selected;
		setFile(selected);

		if (selected) {
			importMutation.mutate({ accountId, commit: false, file: selected });
		}
	};

	const onApprove = () => {
		if (file) {
			importMutation.mutate({ accountId, commit: true, file });
		}
	};

	const onRequestClose = () => {
		reset();
		onClose();
	};

	const displacedUserName = (userId: string) => {
		const user = usersById.get(userId);

		return user ? getDisplayName(user) : 'another user';
	};

	const rows = preview?.rows ?? [];
	const reassignedCount = rows.filter(r => r.outcome === DynamicsUserMapImportOutcome.Reassigned).length;

	// Only outcomes the file actually produced, so the admin is not offered nine empty results to choose between.
	const filterOptions = React.useMemo(() => {
		const all = preview?.rows ?? [];
		const counts = new Map<DynamicsUserMapImportOutcome, number>();
		for (const row of all) {
			counts.set(row.outcome, (counts.get(row.outcome) ?? 0) + 1);
		}
		const unresolved = all.filter(r => !willMap(r.outcome)).length;
		return [
			{ value: ALL_RESULTS, label: `All results (${all.length.toLocaleString()})` },
			...(unresolved > 0
				? [{ value: UNRESOLVED_RESULTS, label: `All unresolved (${unresolved.toLocaleString()})` }]
				: []),
			...[...counts.entries()].map(([outcome, count]) => ({
				value: outcome as string,
				label: `${outcomeLabels[outcome]} (${count.toLocaleString()})`,
			})),
		];
	}, [preview]);

	const visibleRows = React.useMemo(() => {
		const all = preview?.rows ?? [];
		if (rowFilter === ALL_RESULTS) {
			return all;
		}
		if (rowFilter === UNRESOLVED_RESULTS) {
			return all.filter(r => !willMap(r.outcome));
		}
		return all.filter(r => r.outcome === rowFilter);
	}, [preview, rowFilter]);

	// One entry per Dynamics owner record rather than per file row: an advisor who owns contacts as both a user and a
	// team is two records, and the preview has to show what each becomes rather than footnoting the second.
	const visibleEntries = React.useMemo(
		() =>
			visibleRows.flatMap(row => [
				{ ...row, key: `row-${row.rowNumber}`, isLinkedRecord: false },
				...(row.linkedOwners ?? []).map((linkedOwner, index) => ({
					...row,
					key: `row-${row.rowNumber}-linked-${index}`,
					isLinkedRecord: true,
					owner: linkedOwner,
				})),
			]),
		[visibleRows]
	);

	return (
		<Dialog open={isOpen} onOpenChange={open => (!open ? onRequestClose() : undefined)}>
			<DialogPopup styles={[styleSheet.popup]}>
				<DialogTitle>Import user mappings from spreadsheet</DialogTitle>
				<DialogBody>
					{hasUnsavedMappingEdits ? (
						<div className={css(styleSheet.blockedNotice)}>
							The import saves the mappings as soon as you apply them. You have unsaved mappings that would override the
							import if saved afterwards. Please save or undo the pending changes before importing.
						</div>
					) : (
						<>
							<div className={css(styleSheet.fileSection)}>
								<Button
									variant='secondary'
									size='sm'
									disabled={importMutation.isPending}
									onClick={() => fileInputRef.current?.click()}
								>
									{file ? 'Change file' : 'Choose file'}
								</Button>
								<input
									ref={fileInputRef}
									type='file'
									accept={ACCEPTED_FILE_TYPES}
									className={css(styleSheet.hiddenFileInput)}
									disabled={importMutation.isPending}
									onChange={onFileSelected}
								/>
								<span className={css(styleSheet.fileName)}>
									{file ? file.name : 'Expects Owner and Levitate User columns'}
								</span>
							</div>

							{importMutation.isPending ? <LoadingSpinner type='large' /> : null}

							{preview && !importMutation.isPending ? (
								<>
									<div className={css(styleSheet.summary)}>
										<div className={css(styleSheet.summaryItem)}>
											<span className={css(styleSheet.summaryLabel)}>Rows</span>
											<span className={css(styleSheet.summaryValue)}>{preview.totalRows.toLocaleString()}</span>
										</div>
										<div className={css(styleSheet.summaryItem)}>
											<span className={css(styleSheet.summaryLabel)}>Will map</span>
											<span className={css(styleSheet.summaryValue)}>{preview.mappedCount.toLocaleString()}</span>
										</div>
										{reassignedCount > 0 ? (
											<div className={css(styleSheet.summaryItem)}>
												<span className={css(styleSheet.summaryLabel)}>Reassigning</span>
												<span className={css(styleSheet.summaryValue, styleSheet.reassignedValue)}>
													{reassignedCount.toLocaleString()}
												</span>
											</div>
										) : null}
										<div className={css(styleSheet.summaryItem)}>
											<span className={css(styleSheet.summaryLabel)}>Unresolved</span>
											<span className={css(styleSheet.summaryValue, styleSheet.unresolvedValue)}>
												{preview.unresolvedCount.toLocaleString()}
											</span>
										</div>
									</div>

									<div className={css(styleSheet.filterBar)}>
										<span className={css(styleSheet.filterLabel)}>Result:</span>
										<Select items={filterOptions} value={rowFilter} onValueChange={setRowFilter}>
											<SelectTrigger>
												<SelectValue placeholder='All results' />
											</SelectTrigger>
											<SelectContent>
												{filterOptions.map(option => (
													<SelectItem key={option.value} value={option.value}>
														{option.label}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>

									<div className={css(styleSheet.rows)} data-testid='import-preview-rows'>
										<div className={css(styleSheet.row, styleSheet.headerRow)}>
											<span>Row</span>
											<span>Owner</span>
											<span>Levitate User</span>
											<span>Result</span>
										</div>
										{visibleEntries.map(entry => (
											<div key={entry.key} className={css(styleSheet.row)}>
												<span className={css(styleSheet.rowNumber)}>
													{entry.isLinkedRecord ? (
														<span className={css(styleSheet.linkedMarker)} aria-hidden>
															↳
														</span>
													) : (
														entry.rowNumber
													)}
												</span>
												<div className={css(styleSheet.cell)}>
													<span className={css(styleSheet.truncate)} title={entry.owner}>
														{entry.owner || '—'}
													</span>
													{entry.isLinkedRecord ? (
														<span className={css(styleSheet.secondaryLine)}>same advisor as row {entry.rowNumber}</span>
													) : null}
												</div>
												<div className={css(styleSheet.cell)}>
													<span className={css(styleSheet.truncate)} title={entry.levitateUser}>
														{entry.levitateUser || '—'}
													</span>
													{!entry.isLinkedRecord && entry.previousUserId ? (
														<span className={css(styleSheet.secondaryLine, styleSheet.truncate)}>
															was {displacedUserName(entry.previousUserId)}
														</span>
													) : null}
												</div>
												<Badge text={outcomeLabels[entry.outcome]} foregroundColor={outcomeColor(entry.outcome)} />
											</div>
										))}
									</div>
								</>
							) : null}
						</>
					)}
				</DialogBody>
				<DialogFooter>
					<Button variant='secondary' onClick={onRequestClose} disabled={importMutation.isPending}>
						{hasUnsavedMappingEdits ? 'Close' : 'Cancel'}
					</Button>
					{hasUnsavedMappingEdits ? null : (
						<Button
							variant='primary'
							onClick={onApprove}
							disabled={!preview || preview.mappedCount === 0 || importMutation.isPending}
							isLoading={importMutation.isPending}
						>
							{preview ? `Apply ${preview.mappedCount.toLocaleString()} mappings` : 'Apply mappings'}
						</Button>
					)}
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};
